import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { LoadMoreText } from "components/AutoComplete/ReactAutoComplete";
import ButtonCancel from "components/Buttons/ButtonCancel";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiTextFieldColor from "components/MuiExtras/CustomInput/MuiTextFieldColor";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import { UserShortView } from "models/ApiModels";
import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";

///////////////////////////////////////////////////////////////////////////////

export interface ILogTranslated {
    content: string | JSX.Element;
    creationDate: Date;
    creator: UserShortView;
    name: string;
}

interface IProps {
    title?: React.ReactNode;
    maxSuggestionCount: number;
    alreadySelectedItems: AutoCompleteDataModel[];
    onClose: (item: AutoCompleteDataModel[]) => void;
    onGetSuggestions: (searchText: string, offset: number, take: number) => Promise<AutoCompleteDataModel[]>;
}

interface IState {
    searchText: string;
    loading: boolean;
    lastError: any;
    suggestionLoadMore: boolean;
    searchSkipRecords: number;
    autoCompleteItems: AutoCompleteDataModel[];
    selected: AutoCompleteDataModel[];
    lastOffset: number;
}

type Props = IProps & WithTranslation;

///////////////////////////////////////////////////////////////////////////////

class ReactAutoCompleteFallbackDialog extends React.PureComponent<Props, IState> {

    private searchRef: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            loading: false,
            lastError: null,
            autoCompleteItems: null,
            selected: [],
            suggestionLoadMore: false,
            searchSkipRecords: 0,
            lastOffset: 0,
            searchText: ""
        };
    }

    ///
    private load(offset: number) {
        this.setState({ loading: true });

        this.props.onGetSuggestions(this.state.searchText, offset, this.props.maxSuggestionCount + 1)
            .then((data) => {
                var records = data.slice(0, this.props.maxSuggestionCount);
                this.setState({
                    loading: false,
                    autoCompleteItems: (offset == 0 ? records : [...this.state.autoCompleteItems, ...records]),
                    searchSkipRecords: (offset + records.length),
                    suggestionLoadMore: (data.length > records.length),
                    lastOffset: offset
                });
            })
            .catch((error) => {
                this.setState({
                    autoCompleteItems: [],
                    loading: false,
                    suggestionLoadMore: false,
                    lastError: error.canceled ? null : error
                });
            });
    }

    ///
    private handleToggle = (item: AutoCompleteDataModel) => () => {
        const currentIndex = this.state.selected.findIndex(x => x.key == item.key);
        const newChecked = [...this.state.selected];

        if (currentIndex === -1) {
            newChecked.push(item);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        this.setState({ selected: newChecked });
    };

    ///
    private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: e.currentTarget.value });
    };

    ///
    private handleFilterClear = () => {
        this.setState({ searchText: "", autoCompleteItems: null },
            () => {
                this.searchRef.focus();
            }
        );
    };

    ///
    private renderSearch() {
        return (
            <MuiTextFieldColor
                autoFocus
                fullWidth
                placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                value={this.state.searchText}
                onChange={this.handleFilterChange}
                onKeyDown={(e) => { if (e.keyCode == 13) this.load(0); }}
                endAdornment={
                    this.state.searchText == ""
                        ? null
                        : <IconButtonCancel size="small" color="error" onClick={this.handleFilterClear} />
                }
                inputRef={(ref) => this.searchRef = ref}
            />
        );
    }

    private renderItems() {
        const { autoCompleteItems, loading, suggestionLoadMore, selected } = this.state;
        return (
            <>
                {
                    !loading && autoCompleteItems?.length == 0
                        ? <MuiMessageBox variant="info" message={this.props.t("Components.AutoComplete.SuggestionEmptyList")} />
                        : autoCompleteItems != null
                            ? (
                                <List id="aList">
                                    {
                                        autoCompleteItems.map((v, index) => {
                                            const alreadySelected = this.props.alreadySelectedItems.findIndex(x => x.key == v.key) > -1;
                                            return (
                                                <ListItem
                                                    dense
                                                    button
                                                    key={v.key}
                                                    role={undefined}
                                                    disabled={alreadySelected}
                                                    aria-disabled={alreadySelected}
                                                    aria-label={alreadySelected ? this.props.t("Components.AutoComplete.AlreadySelected") : null}
                                                    onClick={this.handleToggle(v)}
                                                    autoFocus={index == this.state.lastOffset}
                                                >
                                                    <ListItemIcon>
                                                        <Checkbox
                                                            edge="start"
                                                            checked={selected.findIndex(x => x.key == v.key) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            inputProps={{ 'aria-labelledby': v.label }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText primary={v.label} />
                                                </ListItem>
                                            );
                                        })
                                    }

                                    {
                                        suggestionLoadMore &&
                                        <Button
                                            onClick={() => { this.load(this.state.searchSkipRecords); }}
                                        >
                                            <LoadMoreText count={this.props.maxSuggestionCount} />
                                        </Button>
                                    }

                                </List>
                            )
                            : null
                }

                {loading && <SpinnerIndicator />}
            </>
        );
    }

    render() {

        return (
            <Dialog
                open
                fullWidth={true}
                maxWidth="md"
                onClose={() => this.props.onClose(this.state.selected)}
            >
                <DialogTitle>{this.props.title || this.props.t("Components.AutoComplete.FallbackTitle")}</DialogTitle>

                <DialogContent style={{ minHeight: "50vh" }}>
                    {
                        this.state.lastError != null
                            ? <RequestErrorReport error={this.state.lastError} />
                            : (
                                <>
                                    {this.renderSearch()}
                                    {this.renderItems()}
                                </>
                            )
                    }
                </DialogContent>
                <DialogActions>
                    <ButtonCancel onClick={() => this.props.onClose(null)} />
                    {
                        this.state.selected?.length > 0 &&
                        <MuiRegularButton color="secondary" onClick={() => this.props.onClose(this.state.selected)}>{this.props.t("Components.AutoComplete.ReturnSelectedItems", { count: this.state.selected.length })}</MuiRegularButton>
                    }
                </DialogActions>
            </Dialog>
        );
    }
}

export default withTranslation()(ReactAutoCompleteFallbackDialog);